import React from "react";
import { useTranslation } from "react-i18next";
import { Image, Spin, Upload } from "antd";
import { CameraOutlined } from "@ant-design/icons";
import ImgCrop from "antd-img-crop";

import { API_URL, getCredentials } from "../../shared/utils/Util";
import { fetchT } from "../../shared/utils/TypedUtil";
import { UpdateItemSchemaResponse } from "../../types/api";
import { ImageManifest, ItemSchema } from "../../types/model";
import { useNotification } from "../../shared/hooks/useNotification";

type ImageUploadCellProps = {
  restaurantId: string;
  record: ItemSchema;
  onSuccess: (updated: ItemSchema) => void;
};

const getImageUrl = (record: ItemSchema): string | undefined => {
  return record.image_manifest?.default.url ?? record.image_url;
};

type ImageUploadTriggerProps = {
  restaurantId: string;
  imageUrl?: string;
  alt: string;
  // Called with the uploaded image. The caller persists it and resolves
  // (or rejects) when done; the trigger shows a spinner until then.
  onUploaded: (manifest: ImageManifest, s3Url: string) => Promise<void>;
  width?: number;
  height?: number;
};

// A compact click-to-upload image thumbnail (with crop), shared by the item
// and combo rows of the menu editor and the combo modal.
export const ImageUploadTrigger: React.FC<ImageUploadTriggerProps> = ({
  restaurantId,
  imageUrl,
  alt,
  onUploaded,
  width = 60,
  height = 40,
}) => {
  const { t } = useTranslation();
  const { notifyError } = useNotification();
  const [busy, setBusy] = React.useState(false);

  const handleUploadChange = (info: {
    file: { status?: string; response?: { manifest: ImageManifest; s3_url: string } };
  }) => {
    if (info.file.status === "uploading") {
      setBusy(true);
    } else if (info.file.status === "done") {
      const manifest = info.file.response?.manifest;
      const s3Url = info.file.response?.s3_url;
      if (manifest && s3Url) {
        onUploaded(manifest, s3Url)
          .catch((err) => notifyError(t("error"), err))
          .finally(() => setBusy(false));
      } else {
        setBusy(false);
        notifyError(t("error"), "Invalid upload response");
      }
    } else if (info.file.status === "error") {
      setBusy(false);
      notifyError(t("error"), info.file.response);
    }
  };

  const trigger = busy ? (
    <Spin
      size="small"
      style={{
        width,
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    />
  ) : imageUrl ? (
    <Image
      src={imageUrl}
      alt={alt}
      width={width}
      height={height}
      style={{ objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
      preview={false}
    />
  ) : (
    <div
      style={{
        width,
        height,
        borderRadius: 4,
        border: "1px dashed #d9d9d9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: "#bfbfbf",
      }}
    >
      <CameraOutlined />
    </div>
  );

  return (
    <ImgCrop aspect={3 / 2} grid quality={1} rotate minZoom={0.1} fillColor="black">
      <Upload
        name="img"
        headers={{ Authorization: `Basic ${getCredentials()}` }}
        accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
        showUploadList={false}
        action={`${API_URL}/v1/r/${restaurantId}/user_content/item_images`}
        disabled={busy}
        onChange={handleUploadChange}
      >
        {trigger}
      </Upload>
    </ImgCrop>
  );
};

export const ImageUploadCell: React.FC<ImageUploadCellProps> = ({
  restaurantId,
  record,
  onSuccess,
}) => {
  const saveImageToSchema = (manifest: ImageManifest, s3Url: string): Promise<void> => {
    const updatedSchema: ItemSchema = {
      ...record,
      image_manifest: manifest,
      image_url: s3Url,
    };

    if (updatedSchema.action_schemas) {
      updatedSchema.action_schema_ids = updatedSchema.action_schemas.map((a) => a.id);
    }
    if (updatedSchema.modification_schemas) {
      updatedSchema.modification_schema_ids = updatedSchema.modification_schemas.map((m) => m.id);
    }

    return new Promise((resolve, reject) => {
      fetchT<UpdateItemSchemaResponse>(
        "PUT",
        `${API_URL}/v1/r/${restaurantId}/item_schemas/${record.id}`,
        updatedSchema,
        () => {
          onSuccess(updatedSchema);
          resolve();
        },
        reject
      );
    });
  };

  return (
    <ImageUploadTrigger
      restaurantId={restaurantId}
      imageUrl={getImageUrl(record)}
      alt={record.name}
      onUploaded={saveImageToSchema}
    />
  );
};
